import { create } from 'zustand';
import {
  markObjectUpdatesConsumed,
  markObjectUpdateFieldsResolved,
  getResolvedObjectUpdateFields,
} from './consumedObjectUpdatesStore';
import type { EditableFields, SearchRunResult } from '@terra/ui-design-system';
import type {
  AssetTypeType,
  PocBlock,
} from '@terra/shared-schemas-orchestrator';
import { resolveAcceptedFindingSuggestionValue } from '../utils/findingSuggestionFieldUtils';
import {
  isFindingEditableField,
  normalizeFindingPatch,
  type FindingEditableField,
} from '../utils/findingEditableFieldKeys';
import { foldPocTextIntoPocBlocks } from '../utils/foldPocTextIntoPocBlocks';
import { mergeStagedPocBlocksOntoEditorDraft } from './mergeStagedPocBlocksOntoEditorDraft';

type SearchSignalResult = {
  id: string;
  label: string;
  category?: string;
  description?: string;
  status?: string;
  createdAt?: string;
};

type SearchAssetResult = {
  id: string;
  url: string;
  method?: string;
  type: AssetTypeType;
};

export type { FindingEditableField } from '../utils/findingEditableFieldKeys';

export type FindingDraftUpdateEffect = {
  fields: FindingEditableField[];
  timestamp: number;
};

export type PendingAiFieldEdit = {
  field: FindingEditableField;
  before: unknown;
  after: unknown;
  sourceUpdateId?: string;
};

export type PendingAiEdit = {
  fields: PendingAiFieldEdit[];
  reason?: string;
  timestamp: number;
};

type FindingEditState = {
  mode: 'view' | 'edit';
  editableFields: EditableFields;
  originalFields: EditableFields | null;
  hasUnsavedChanges: boolean;
  signalSearchResults?: SearchSignalResult[];
  runSearchResults?: SearchRunResult[];
  assetSearchResults?: SearchAssetResult[];
};

type FindingEditStore = {
  findingEditStates: Record<string, FindingEditState>;
  updateEffects: Record<string, FindingDraftUpdateEffect>;
  pendingAiEdits: Record<string, PendingAiEdit>;
  setFindingEditState: (
    findingId: string,
    state: Partial<FindingEditState>,
  ) => void;
  clearFindingEditState: (findingId: string) => void;
  clearFindingEditDraftAfterSave: (findingId: string) => void;
  getFindingEditState: (findingId: string) => FindingEditState | undefined;
  stageAiPatch: (
    findingId: string,
    {
      patch,
      reason,
      baseFields,
      sourceUpdateId,
    }: {
      patch: Partial<EditableFields>;
      reason?: string;
      baseFields?: EditableFields;
      sourceUpdateId?: string;
    },
  ) => void;
  acceptAiEdits: (
    findingId: string,
    fields?: FindingEditableField[],
    options?: {
      baseEditableFields: EditableFields;
      originalFields?: EditableFields | null;
    },
  ) => void;
  discardAiEdits: (findingId: string, fields?: FindingEditableField[]) => void;
  getPendingAiEdits: (findingId: string) => PendingAiEdit | undefined;
  getUpdateEffect: (findingId: string) => FindingDraftUpdateEffect | undefined;
  getAcceptedObjectUpdateSourceIds: (findingId: string) => string[];
  consumeAcceptedObjectUpdateSourceIds: (findingId: string) => string[];
  acceptedObjectUpdateSourceIds: Record<string, string[]>;
};

export const defaultEditableFields: EditableFields = {
  title: '',
  shortDescription: '',
  description: '',
  poc: '',
  pocBlocks: null,
  businessImpact: '',
  mitigationPlan: '',
  status: 'Unresolved',
  severity: 'Low',
  category: null,
  assignedTo: '',
  signalId: null,
  traceThreadId: null,
  assets: [],
  isPublished: null,
  findingUpdateComment: null,
  cvss: null,
  owaspRisk: null,
};

const defaultEditState: FindingEditState = {
  mode: 'view',
  editableFields: defaultEditableFields,
  originalFields: null,
  hasUnsavedChanges: false,
  signalSearchResults: [],
  runSearchResults: [],
  assetSearchResults: [],
};

function mergePendingFieldEdits({
  existing,
  patch,
  baseFields,
  sourceUpdateId,
}: {
  existing: PendingAiFieldEdit[];
  patch: Partial<EditableFields>;
  baseFields?: EditableFields;
  sourceUpdateId?: string;
}): PendingAiFieldEdit[] {
  const byField = new Map<FindingEditableField, PendingAiFieldEdit>(
    existing.map((entry) => [entry.field, entry]),
  );

  for (const [key, value] of Object.entries(patch)) {
    if (!isFindingEditableField(key) || value === undefined) {
      continue;
    }

    const existingEntry = byField.get(key);
    const before = existingEntry?.before ?? baseFields?.[key];

    byField.set(key, {
      field: key,
      before,
      after: value,
      sourceUpdateId: sourceUpdateId ?? existingEntry?.sourceUpdateId,
    });
  }

  return Array.from(byField.values());
}

function uniqueSourceUpdateIds(ids: Array<string | undefined>): string[] {
  return [...new Set(ids.filter((id): id is string => Boolean(id)))];
}

function collectSourceUpdateIds(entries: PendingAiFieldEdit[]): string[] {
  return uniqueSourceUpdateIds(entries.map((entry) => entry.sourceUpdateId));
}

function filterFullyResolvedSourceUpdateIds({
  resolvedEntries,
  remainingEntries,
}: {
  resolvedEntries: PendingAiFieldEdit[];
  remainingEntries: PendingAiFieldEdit[];
}): string[] {
  const resolvedSourceUpdateIds = collectSourceUpdateIds(resolvedEntries);
  const remainingSourceUpdateIds = new Set(
    collectSourceUpdateIds(remainingEntries),
  );

  return resolvedSourceUpdateIds.filter(
    (sourceUpdateId) => !remainingSourceUpdateIds.has(sourceUpdateId),
  );
}

export const useFindingEditStore = create<FindingEditStore>((set, get) => ({
  findingEditStates: {},
  updateEffects: {},
  pendingAiEdits: {},
  acceptedObjectUpdateSourceIds: {},

  setFindingEditState: (findingId, state) => {
    set((store) => ({
      findingEditStates: {
        ...store.findingEditStates,
        [findingId]: {
          ...(store.findingEditStates[findingId] ?? defaultEditState),
          ...state,
        },
      },
    }));
  },

  clearFindingEditState: (findingId) => {
    set((state) => {
      const { [findingId]: _removedDraft, ...findingEditStates } =
        state.findingEditStates;
      const { [findingId]: _removedEffect, ...updateEffects } =
        state.updateEffects;
      const { [findingId]: _removedPending, ...pendingAiEdits } =
        state.pendingAiEdits;
      const {
        [findingId]: _removedAcceptedSourceIds,
        ...acceptedObjectUpdateSourceIds
      } = state.acceptedObjectUpdateSourceIds;

      return {
        findingEditStates,
        updateEffects,
        pendingAiEdits,
        acceptedObjectUpdateSourceIds,
      };
    });
  },

  clearFindingEditDraftAfterSave: (findingId) => {
    set((state) => {
      const { [findingId]: _removedDraft, ...findingEditStates } =
        state.findingEditStates;
      const { [findingId]: _removedEffect, ...updateEffects } =
        state.updateEffects;
      const {
        [findingId]: _removedAcceptedSourceIds,
        ...acceptedObjectUpdateSourceIds
      } = state.acceptedObjectUpdateSourceIds;

      return {
        findingEditStates,
        updateEffects,
        pendingAiEdits: state.pendingAiEdits,
        acceptedObjectUpdateSourceIds,
      };
    });
  },

  getFindingEditState: (findingId) => {
    return get().findingEditStates[findingId];
  },

  stageAiPatch: (findingId, { patch, reason, baseFields, sourceUpdateId }) => {
    const normalizedPatch = normalizeFindingPatch(patch);

    if (sourceUpdateId) {
      const resolvedFields = getResolvedObjectUpdateFields(sourceUpdateId);

      for (const field of Object.keys(normalizedPatch)) {
        if (resolvedFields.has(field)) {
          delete normalizedPatch[field as FindingEditableField];
        }
      }
    }

    const updatedFields = Object.keys(
      normalizedPatch,
    ) as FindingEditableField[];

    if (updatedFields.length === 0) {
      return;
    }

    const existingPending = get().pendingAiEdits[findingId];
    const editState = get().findingEditStates[findingId];
    const resolvedBaseFields = baseFields ?? editState?.editableFields;
    const pendingPocBlocks = existingPending?.fields.find(
      (entry) => entry.field === 'pocBlocks',
    )?.after as PocBlock[] | null | undefined;
    const pendingPoc = existingPending?.fields.find(
      (entry) => entry.field === 'poc',
    )?.after;
    const patchWithPendingPoc =
      normalizedPatch.pocBlocks !== undefined &&
      normalizedPatch.poc === undefined &&
      typeof pendingPoc === 'string'
        ? { ...normalizedPatch, poc: pendingPoc }
        : normalizedPatch;
    const foldedPatch = foldPocTextIntoPocBlocks({
      patch: patchWithPendingPoc,
      currentPocBlocks:
        pendingPocBlocks !== undefined
          ? pendingPocBlocks
          : resolvedBaseFields?.pocBlocks,
    });
    const draftPocBlocks =
      pendingPocBlocks !== undefined
        ? pendingPocBlocks
        : resolvedBaseFields?.pocBlocks;
    const savedPocBlocks = editState?.originalFields?.pocBlocks;
    const patchWithDraftOrder =
      Array.isArray(foldedPatch.pocBlocks) &&
      Array.isArray(draftPocBlocks) &&
      draftPocBlocks.length > 0
        ? {
            ...foldedPatch,
            pocBlocks: mergeStagedPocBlocksOntoEditorDraft({
              draft: draftPocBlocks,
              saved:
                Array.isArray(savedPocBlocks) && savedPocBlocks.length > 0
                  ? savedPocBlocks
                  : draftPocBlocks,
              staged: foldedPatch.pocBlocks,
            }),
          }
        : foldedPatch;
    const existingFields =
      patchWithPendingPoc.poc !== undefined &&
      patchWithDraftOrder.poc === undefined
        ? (existingPending?.fields ?? []).filter(
            (entry) => entry.field !== 'poc',
          )
        : (existingPending?.fields ?? []);
    const nextFields = mergePendingFieldEdits({
      existing: existingFields,
      patch: patchWithDraftOrder,
      baseFields: resolvedBaseFields,
      sourceUpdateId,
    });

    set((state) => ({
      pendingAiEdits: {
        ...state.pendingAiEdits,
        [findingId]: {
          fields: nextFields,
          reason: reason ?? existingPending?.reason,
          timestamp: Date.now(),
        },
      },
    }));
  },

  acceptAiEdits: (findingId, fields, options) => {
    const pending = get().pendingAiEdits[findingId];

    if (!pending || pending.fields.length === 0) {
      return;
    }

    const baseEditableFields = options?.baseEditableFields;

    if (!baseEditableFields) {
      return;
    }

    const originalFields = options?.originalFields;
    const fieldsToAccept = fields
      ? pending.fields.filter((entry) => fields.includes(entry.field))
      : pending.fields;

    if (fieldsToAccept.length === 0) {
      return;
    }

    const currentState = get().findingEditStates[findingId] || defaultEditState;
    const nextEditableFields = { ...baseEditableFields };
    const acceptedFieldKeys: FindingEditableField[] = [];

    for (const entry of fieldsToAccept) {
      const currentValue = baseEditableFields[entry.field];
      const acceptedValue = resolveAcceptedFindingSuggestionValue({
        current: currentValue,
        before: entry.before,
        after: entry.after,
        original: originalFields?.[entry.field],
      });

      Object.assign(nextEditableFields, {
        [entry.field]: acceptedValue,
      });
      acceptedFieldKeys.push(entry.field);
    }

    const remainingFields = pending.fields.filter(
      (entry) => !acceptedFieldKeys.includes(entry.field),
    );
    const consumedSourceUpdateIds = filterFullyResolvedSourceUpdateIds({
      resolvedEntries: fieldsToAccept,
      remainingEntries: remainingFields,
    });

    if (consumedSourceUpdateIds.length > 0) {
      markObjectUpdatesConsumed(consumedSourceUpdateIds);
    }

    for (const sourceUpdateId of uniqueSourceUpdateIds(
      collectSourceUpdateIds(fieldsToAccept),
    )) {
      markObjectUpdateFieldsResolved({
        sourceUpdateId,
        fields: fieldsToAccept
          .filter((entry) => entry.sourceUpdateId === sourceUpdateId)
          .map((entry) => entry.field),
      });
    }

    set((state) => {
      const nextPendingAiEdits = { ...state.pendingAiEdits };

      if (remainingFields.length === 0) {
        delete nextPendingAiEdits[findingId];
      } else {
        nextPendingAiEdits[findingId] = {
          ...pending,
          fields: remainingFields,
        };
      }

      const existingAcceptedSourceIds =
        state.acceptedObjectUpdateSourceIds[findingId] ?? [];
      const nextAcceptedSourceIds = uniqueSourceUpdateIds([
        ...existingAcceptedSourceIds,
        ...consumedSourceUpdateIds,
      ]);

      return {
        findingEditStates: {
          ...state.findingEditStates,
          [findingId]: {
            ...currentState,
            mode: 'edit',
            editableFields: nextEditableFields,
            hasUnsavedChanges: true,
          },
        },
        pendingAiEdits: nextPendingAiEdits,
        acceptedObjectUpdateSourceIds: {
          ...state.acceptedObjectUpdateSourceIds,
          [findingId]: nextAcceptedSourceIds,
        },
      };
    });
  },

  discardAiEdits: (findingId, fields) => {
    const pending = get().pendingAiEdits[findingId];

    if (!pending) {
      return;
    }

    const discardedEntries = fields
      ? pending.fields.filter((entry) => fields.includes(entry.field))
      : pending.fields;

    const remainingFields = fields
      ? pending.fields.filter((entry) => !fields.includes(entry.field))
      : [];

    const consumedSourceUpdateIds = filterFullyResolvedSourceUpdateIds({
      resolvedEntries: discardedEntries,
      remainingEntries: remainingFields,
    });

    if (consumedSourceUpdateIds.length > 0) {
      markObjectUpdatesConsumed(consumedSourceUpdateIds);
    }

    for (const sourceUpdateId of uniqueSourceUpdateIds(
      collectSourceUpdateIds(discardedEntries),
    )) {
      markObjectUpdateFieldsResolved({
        sourceUpdateId,
        fields: discardedEntries
          .filter((entry) => entry.sourceUpdateId === sourceUpdateId)
          .map((entry) => entry.field),
      });
    }

    set((state) => {
      const nextPendingAiEdits = { ...state.pendingAiEdits };

      if (!fields || remainingFields.length === 0) {
        delete nextPendingAiEdits[findingId];
      } else {
        nextPendingAiEdits[findingId] = {
          ...pending,
          fields: remainingFields,
        };
      }

      return { pendingAiEdits: nextPendingAiEdits };
    });
  },

  getPendingAiEdits: (findingId) => get().pendingAiEdits[findingId],

  getUpdateEffect: (findingId) => get().updateEffects[findingId],

  getAcceptedObjectUpdateSourceIds: (findingId) =>
    get().acceptedObjectUpdateSourceIds[findingId] ?? [],

  consumeAcceptedObjectUpdateSourceIds: (findingId) => {
    const acceptedIds = get().acceptedObjectUpdateSourceIds[findingId] ?? [];
    const pendingSourceIds = new Set(
      collectSourceUpdateIds(get().pendingAiEdits[findingId]?.fields ?? []),
    );
    const sourceIds = acceptedIds.filter(
      (sourceUpdateId) => !pendingSourceIds.has(sourceUpdateId),
    );

    if (sourceIds.length === 0) {
      return [];
    }

    set((state) => {
      const remainingAcceptedIds = acceptedIds.filter((sourceUpdateId) =>
        pendingSourceIds.has(sourceUpdateId),
      );
      const nextAcceptedObjectUpdateSourceIds = {
        ...state.acceptedObjectUpdateSourceIds,
      };

      if (remainingAcceptedIds.length === 0) {
        delete nextAcceptedObjectUpdateSourceIds[findingId];
      } else {
        nextAcceptedObjectUpdateSourceIds[findingId] = remainingAcceptedIds;
      }

      return {
        acceptedObjectUpdateSourceIds: nextAcceptedObjectUpdateSourceIds,
      };
    });

    return sourceIds;
  },
}));
